/**
 * Finance · Expense 360 Workspace (Wave 3).
 *
 * Question this workspace answers:
 *   "Where is money leaking?"
 *
 * Contract-driven per Objects 023 (Expense) · 025 (Vendor Payment) ·
 * 016 (Supplier) · 028 (Budget) · 013 (Purchase Order).
 *
 * Reuses existing primitives only. Fixes the historic
 * HTTP 404 on the expense-voucher document by degrading gracefully
 * when a record has no linked voucher.
 */
import { useMemo, useState } from "react";
import { Receipt } from "lucide-react";
import { Card, SectionHeader, TextInput, DeltaChip, Button } from "../../components/lib";
import { TabBar, ListRow, Drawer, Field, Segmented } from "../../components/lib.overlay";
import { useDeepLink } from "../../lib/integration";
import { DocumentActions } from "../../components/DocumentActions";
import { RecordEditor, FieldSpec } from "../../components/RecordEditor";
import { AuditTrail } from "../../components/AuditTrail";
import { api } from "../../services/api";
import { upsertLocal } from "../../services/store";
import { EXPENSES, EXPENSE_TOTALS, BUDGET, VENDOR_PAYMENTS, Expense, ExpenseCategory } from "../../mocks/financeData";
import { SUPPLIERS } from "../../mocks/operationsData";
import { PROCUREMENT } from "../../mocks/procurementData";
import { COST_CENTERS } from "../../mocks/costCentersData";
import { BATCHES } from "../../mocks/operationsData";

const STATUS_TINT: Record<string, { bg: string; fg: string; label: string }> = {
  paid:       { bg: "var(--ph-success-tint)", fg: "var(--ph-success)", label: "Paid"       },
  reimbursed: { bg: "var(--ph-success-tint)", fg: "var(--ph-success)", label: "Reimbursed" },
  approved:   { bg: "var(--ph-success-tint)", fg: "var(--ph-success)", label: "Approved"   },
  scheduled:  { bg: "var(--ph-warning-tint)", fg: "var(--ph-warning)", label: "Scheduled"  },
  submitted:  { bg: "var(--ph-ai-tint)",      fg: "var(--ph-ai)",      label: "Submitted"  },
  overdue:    { bg: "var(--ph-danger-tint)",  fg: "var(--ph-danger)",  label: "Overdue"    },
  rejected:   { bg: "var(--ph-danger-tint)",  fg: "var(--ph-danger)",  label: "Rejected"   },
  draft:      { bg: "var(--ph-neutral-tint)", fg: "var(--ph-ink-muted)", label: "Draft"    },
};

type ExpTab =
  | "overview" | "vendor" | "costCenter" | "project" | "po"
  | "invoice" | "budget" | "approval" | "attachments"
  | "timeline" | "audit";

export default function Expenses() {
  const { q: initialQ } = useDeepLink();
  const [tab, setTab] = useState<ExpenseCategory | "all">("all");
  const [q, setQ]     = useState(initialQ);
  const [selected, setSelected] = useState<Expense | null>(null);
  const [wsTab, setWsTab] = useState<ExpTab>("overview");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(false);

  const expenseSchema: FieldSpec[] = [
    { key: "vendor",      label: "Vendor",       kind: "text",   placeholder: "e.g. Aramex" },
    { key: "description", label: "Description",  kind: "textarea", placeholder: "Cold-chain shipment · Cairo → Alexandria" },
    { key: "category",    label: "Category",     kind: "select", options: [
      { value: "cogs", label: "COGS" }, { value: "marketing", label: "Marketing" },
      { value: "salaries", label: "Salaries" }, { value: "logistics", label: "Logistics" },
      { value: "rent", label: "Rent" }, { value: "software", label: "Software" },
      { value: "other", label: "Other" },
    ] },
    { key: "amountEGP",   label: "Amount (EGP)", kind: "number", placeholder: "12500" },
    { key: "vatPct",      label: "VAT %",        kind: "number", placeholder: "14" },
    { key: "costCenter",  label: "Cost center",  kind: "text",   placeholder: "Operations" },
    { key: "owner",       label: "Owner",        kind: "text",   placeholder: "e.g. Hassan" },
    { key: "notes",       label: "Notes",        kind: "textarea" },
  ];

  async function transition(status: string) {
    if (!selected) return;
    try {
      const row = await api.transition("expenses", selected.id, status);
      upsertLocal("expenses", row as { id?: string });
      setSelected(row as unknown as Expense);
    } catch (e) {
      window.alert((e as Error).message);
    }
  }

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: EXPENSES.length };
    EXPENSES.forEach((e) => { c[e.category] = (c[e.category] || 0) + 1; });
    return c;
  }, []);

  const filtered = useMemo(
    () => EXPENSES.filter((e) =>
      (tab === "all" || e.category === tab) &&
      (!q || (e.vendor || "").toLowerCase().includes(q.toLowerCase()) || (e.description || "").toLowerCase().includes(q.toLowerCase())),
    ),
    [tab, q],
  );

  const totalMonth = EXPENSE_TOTALS.reduce((n, s) => n + s.monthEGP, 0);
  const maxCat     = Math.max(1, ...EXPENSE_TOTALS.map((c) => c.monthEGP));

  // ── Portfolio-level leakage snapshot ─────────────────────────────
  const overdueCount = EXPENSES.filter((e) => (e.status as string) === "overdue").length;
  const overdueValue = EXPENSES.filter((e) => (e.status as string) === "overdue").reduce((n, e) => n + e.amountEGP, 0);
  const topCat       = [...EXPENSE_TOTALS].sort((a, b) => b.monthEGP - a.monthEGP)[0];

  // ── Drill data for selected expense ──────────────────────────────
  const expData = useMemo(() => {
    if (!selected) return null;
    const supplier = SUPPLIERS.find((s) => s.name === selected.vendor);
    const po = PROCUREMENT.find((p) => p.supplier === selected.vendor && Math.abs(p.amountEGP - selected.amountEGP) <= selected.amountEGP * 0.15)
      || PROCUREMENT.find((p) => p.supplier === selected.vendor);
    const vp = VENDOR_PAYMENTS.find((v) => v.vendor === selected.vendor && Math.abs(v.amountEGP - selected.amountEGP) <= selected.amountEGP * 0.15)
      || VENDOR_PAYMENTS.find((v) => v.vendor === selected.vendor);
    // Cost-center match (mocks may store either `costCenter` label or code)
    const cc = (() => {
      const rec = selected as unknown as { costCenter?: string };
      if (!rec.costCenter) return undefined;
      return COST_CENTERS.find((c) => c.name === rec.costCenter || c.code === rec.costCenter);
    })();
    // Budget-line match (BUDGET.category ↔ ExpenseCategoryTotal.label)
    const catLabel = EXPENSE_TOTALS.find((t) => t.key === selected.category)?.label ?? selected.category;
    const budgetLine = BUDGET.find((b) => (b.category || "").toLowerCase() === (catLabel || "").toLowerCase() || (b.ownerDepartment || "").toLowerCase() === (cc?.name || "").toLowerCase());
    // Batches (project proxy for COGS lines)
    const linkedBatch = selected.category === "cogs" ? BATCHES.find((b) => b.masterPerfumer && (b.masterPerfumer || "").toLowerCase().includes(((selected.vendor || "").split(" ")[0] || "").toLowerCase())) : undefined;
    return { supplier, po, vp, cc, budgetLine, catLabel, linkedBatch };
  }, [selected]);

  return (
    <div className="space-y-6" data-testid="fn-expenses">
      {/* ── Leakage snapshot ─────────────────────────────────────── */}
      <Card testId="fn-exp-summary">
        <SectionHeader eyebrow="This month · Where is money leaking?" title="Expense summary" action={<Receipt size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Total spend</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-danger)" }}>EGP {(totalMonth / 1000000).toFixed(2)}M</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Top category</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-brand-primary)" }}>{topCat?.label ?? "—"}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Overdue count</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: overdueCount > 0 ? "var(--ph-danger)" : "var(--ph-success)" }}>{overdueCount}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Overdue value</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: overdueValue > 0 ? "var(--ph-danger)" : "var(--ph-success)" }}>EGP {(overdueValue / 1000).toFixed(0)}K</div>
          </div>
        </div>
        <div className="space-y-2">
          {EXPENSE_TOTALS.map((c) => {
            const bar = Math.max(2, (c.monthEGP / maxCat) * 100);
            return (
              <div key={c.key} data-testid={`fn-exp-cat-${c.key}`} className="grid items-center gap-3 py-1.5"
                style={{ gridTemplateColumns: "1.4fr 3fr 1fr 0.7fr" }}>
                <button
                  data-testid={`fn-exp-cat-filter-${c.key}`}
                  onClick={() => setTab(c.key as ExpenseCategory)}
                  className="text-sm font-medium text-left"
                  style={{ color: "var(--ph-ink-strong)" }}
                >{c.label}</button>
                <div className="h-2 rounded" style={{ background: "var(--ph-border-subtle)", overflow: "hidden" }}>
                  <div style={{ width: `${bar}%`, height: "100%", background: "var(--ph-brand-primary)" }} />
                </div>
                <div className="text-sm tabular-nums text-right" style={{ color: "var(--ph-ink-strong)" }}>EGP {(c.monthEGP / 1000).toFixed(0)}K</div>
                <div className="flex justify-end"><DeltaChip delta={c.delta} trend={c.trend} /></div>
              </div>
            );
          })}
        </div>
      </Card>

      <Card testId="fn-exp-filters">
        <div className="flex items-center gap-3">
          <div className="flex-1"><TextInput value={q} onChange={setQ} placeholder="Search vendor or description…" testId="fn-exp-search" /></div>
          <Button testId="fn-exp-new" onClick={() => setCreating(true)}>+ New expense</Button>
        </div>
        <div className="mt-4">
          <TabBar<ExpenseCategory | "all">
            testId="fn-exp-tabs"
            active={tab}
            onChange={setTab}
            tabs={[
              { key: "all",       label: "All",       count: counts.all       },
              { key: "cogs",      label: "COGS",      count: counts.cogs      || 0 },
              { key: "marketing", label: "Marketing", count: counts.marketing || 0 },
              { key: "salaries",  label: "Salaries",  count: counts.salaries  || 0 },
              { key: "logistics", label: "Logistics", count: counts.logistics || 0 },
              { key: "rent",      label: "Rent",      count: counts.rent      || 0 },
              { key: "software",  label: "Software",  count: counts.software  || 0 },
              { key: "other",     label: "Other",     count: counts.other     || 0 },
            ]}
          />
        </div>
      </Card>

      <Card padded={false} testId="fn-exp-list-card">
        <div className="p-5 flex items-start justify-between flex-wrap gap-3" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Ledger" title={`${filtered.length} entr${filtered.length === 1 ? "y" : "ies"}`} />
          <DocumentActions docKey="expense-voucher" testId="fn-exp-doc-actions" />
        </div>
        <div className="p-2" data-testid="fn-exp-list">
          {filtered.map((e) => {
            const s = STATUS_TINT[e.status as string] ?? STATUS_TINT.draft;
            return (
              <ListRow
                key={e.id}
                testId={`fn-exp-${e.id}`}
                onClick={() => { setSelected(e); setWsTab("overview"); }}
                label={
                  <span className="inline-flex items-center gap-2">
                    <span>{e.vendor}</span>
                    <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: s.bg, color: s.fg, borderRadius: "var(--ph-radius-sm)" }}>
                      {s.label}
                    </span>
                    <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: "var(--ph-neutral-tint)", color: "var(--ph-ink-muted)", borderRadius: "var(--ph-radius-sm)" }}>
                      {e.category}
                    </span>
                  </span>
                }
                sublabel={<>{e.description} · {e.paidRelative || (e as unknown as { dateRelative?: string }).dateRelative}</>}
                trailing={
                  <div className="text-sm font-semibold tabular-nums" style={{ color: "var(--ph-ink-strong)" }}>
                    EGP {e.amountEGP.toLocaleString()}
                  </div>
                }
              />
            );
          })}
        </div>
      </Card>

      <Drawer open={Boolean(selected)} onClose={() => setSelected(null)} title="Expense 360" testId="fn-exp-drawer" width={720}>
        {selected && expData && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: STATUS_TINT[selected.status as string]?.fg ?? "var(--ph-ink-muted)" }}>
              {STATUS_TINT[selected.status as string]?.label ?? selected.status}
            </div>
            <div style={{ fontFamily: "var(--ph-display-font)", fontSize: "1.2rem", color: "var(--ph-ink-strong)" }} className="mt-1 mb-3">{selected.vendor}</div>
            <div className="mb-4 pb-3" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
              <DocumentActions docKey="expense-voucher" recordId={selected.id} testId={`fn-exp-drawer-single-${selected.id}`} compact />
            </div>

            <div className="mb-4 overflow-x-auto">
              <Segmented<ExpTab>
                testId="fn-exp-ws-tabs"
                value={wsTab}
                onChange={setWsTab}
                options={[
                  { value: "overview",    label: "Overview"    },
                  { value: "vendor",      label: "Vendor"      },
                  { value: "costCenter",  label: "Cost Ctr"    },
                  { value: "project",     label: "Project"     },
                  { value: "po",          label: "PO"          },
                  { value: "invoice",     label: "Invoice"     },
                  { value: "budget",      label: "Budget"      },
                  { value: "approval",    label: "Approval"    },
                  { value: "attachments", label: "Attachments" },
                  { value: "timeline",    label: "Timeline"    },
                  { value: "audit",       label: "Audit"       },
                ]}
              />
            </div>

            {wsTab === "overview" && (
              <div data-testid="fn-exp-ws-overview">
                <Field label="Category"     inline><span className="text-sm">{expData.catLabel}</span></Field>
                <Field label="Amount"       inline><span className="text-sm font-semibold tabular-nums">EGP {selected.amountEGP.toLocaleString()}</span></Field>
                <Field label="VAT"          inline><span className="text-sm tabular-nums">{(selected as unknown as { vatPct?: number }).vatPct ?? 14}%</span></Field>
                <Field label="Cost centre"  inline><span className="text-sm">{(selected as unknown as { costCenter?: string }).costCenter || "—"}</span></Field>
                <Field label="Owner"        inline><span className="text-sm">{(selected as unknown as { owner?: string }).owner || "—"}</span></Field>
                <Field label="Description"><span className="text-sm">{selected.description}</span></Field>
              </div>
            )}

            {wsTab === "vendor" && (
              <div data-testid="fn-exp-ws-vendor">
                <Field label="Vendor" inline><span className="text-sm font-semibold">{selected.vendor}</span></Field>
                {expData.supplier ? (
                  <>
                    <Field label="City"        inline><span className="text-sm">{expData.supplier.city}</span></Field>
                    <Field label="Category"    inline><span className="text-sm">{expData.supplier.category}</span></Field>
                    <Field label="On-time"     inline><span className="text-sm tabular-nums">{Math.round(expData.supplier.onTimeRate * 100)}%</span></Field>
                    <Field label="Risk"        inline><span className="text-sm">{expData.supplier.risk}</span></Field>
                  </>
                ) : (
                  <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>Vendor not in supplier master. Consider onboarding this vendor if spend is recurring.</div>
                )}
              </div>
            )}

            {wsTab === "costCenter" && (
              <div data-testid="fn-exp-ws-cc">
                {expData.cc ? (
                  <>
                    <Field label="Cost centre" inline><span className="text-sm font-semibold">{expData.cc.name}</span></Field>
                    <Field label="Code"        inline><span className="text-sm font-mono">{expData.cc.code}</span></Field>
                    <Field label="Owner"       inline><span className="text-sm">{expData.cc.owner}</span></Field>
                    <Field label="Budget"      inline><span className="text-sm tabular-nums">EGP {expData.cc.budgetEGP.toLocaleString()}</span></Field>
                    <Field label="Spent"       inline><span className="text-sm tabular-nums">EGP {expData.cc.spentEGP.toLocaleString()}</span></Field>
                    <Field label="Utilisation" inline><span className="text-sm tabular-nums" style={{ color: expData.cc.utilizationPct >= 100 ? "var(--ph-danger)" : "var(--ph-success)" }}>{expData.cc.utilizationPct}%</span></Field>
                  </>
                ) : (
                  <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No cost-centre linked to this expense.</div>
                )}
              </div>
            )}

            {wsTab === "project" && (
              <div data-testid="fn-exp-ws-project">
                {expData.linkedBatch ? (
                  <>
                    <Field label="Batch"     inline><span className="text-sm font-mono">{expData.linkedBatch.code}</span></Field>
                    <Field label="Product"   inline><span className="text-sm">{expData.linkedBatch.productName}</span></Field>
                    <Field label="Stage"     inline><span className="text-sm">{expData.linkedBatch.stage}</span></Field>
                    <Field label="Units"     inline><span className="text-sm tabular-nums">{expData.linkedBatch.units}</span></Field>
                  </>
                ) : (
                  <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No batch/project linked. Category &ldquo;{expData.catLabel}&rdquo; typically maps to OpEx rather than a project cost.</div>
                )}
              </div>
            )}

            {wsTab === "po" && (
              <div data-testid="fn-exp-ws-po">
                {expData.po ? (
                  <>
                    <Field label="PO #"         inline><span className="text-sm font-mono">{expData.po.poNumber}</span></Field>
                    <Field label="Supplier"     inline><span className="text-sm">{expData.po.supplier}</span></Field>
                    <Field label="Status"       inline><span className="text-sm">{expData.po.status}</span></Field>
                    <Field label="PO amount"    inline><span className="text-sm tabular-nums">EGP {expData.po.amountEGP.toLocaleString()}</span></Field>
                    <Field label="Ordered"      inline><span className="text-sm">{expData.po.orderedRelative}</span></Field>
                    <Field label="Expected"     inline><span className="text-sm">{expData.po.expectedRelative}</span></Field>
                    <Field label="Variance"     inline><span className="text-sm tabular-nums" style={{ color: (selected.amountEGP - expData.po.amountEGP) === 0 ? "var(--ph-success)" : "var(--ph-warning)" }}>{selected.amountEGP - expData.po.amountEGP >= 0 ? "+" : ""}EGP {(selected.amountEGP - expData.po.amountEGP).toLocaleString()}</span></Field>
                  </>
                ) : (
                  <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No purchase order linked to this expense. Non-PO spend — verify against approved vendor list.</div>
                )}
              </div>
            )}

            {wsTab === "invoice" && (
              <div data-testid="fn-exp-ws-inv">
                {expData.vp ? (
                  <>
                    <Field label="Payment ref"   inline><span className="text-sm font-mono">{expData.vp.reference}</span></Field>
                    <Field label="Method"        inline><span className="text-sm">{expData.vp.method}</span></Field>
                    <Field label="Status"        inline><span className="text-sm">{expData.vp.status}</span></Field>
                    <Field label="Payment amount" inline><span className="text-sm tabular-nums">EGP {expData.vp.amountEGP.toLocaleString()}</span></Field>
                    <Field label="Due"           inline><span className="text-sm">{expData.vp.dueRelative}</span></Field>
                    <Field label="Approved by"   inline><span className="text-sm">{expData.vp.approvedBy || "—"}</span></Field>
                  </>
                ) : (
                  <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No vendor-invoice/receipt payment recorded. If expense is paid, book a matching vendor payment.</div>
                )}
              </div>
            )}

            {wsTab === "budget" && (
              <div data-testid="fn-exp-ws-budget">
                {expData.budgetLine ? (() => {
                  const b = expData.budgetLine;
                  const util = b.plannedEGP ? (b.actualEGP / b.plannedEGP) * 100 : 0;
                  const impact = b.plannedEGP ? (selected.amountEGP / b.plannedEGP) * 100 : 0;
                  return (
                    <>
                      <Field label="Budget line"      inline><span className="text-sm font-semibold">{b.category}</span></Field>
                      <Field label="Owner dept"       inline><span className="text-sm">{b.ownerDepartment}</span></Field>
                      <Field label="Planned"          inline><span className="text-sm tabular-nums">EGP {b.plannedEGP.toLocaleString()}</span></Field>
                      <Field label="Actual"           inline><span className="text-sm tabular-nums">EGP {b.actualEGP.toLocaleString()}</span></Field>
                      <Field label="Utilisation"      inline><span className="text-sm tabular-nums" style={{ color: util >= 100 ? "var(--ph-danger)" : "var(--ph-success)" }}>{util.toFixed(0)}%</span></Field>
                      <Field label="This expense hit" inline><span className="text-sm tabular-nums" style={{ color: impact >= 20 ? "var(--ph-warning)" : "var(--ph-ink-strong)" }}>{impact.toFixed(1)}% of plan</span></Field>
                    </>
                  );
                })() : (
                  <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No matching budget line found for this category. Consider creating one to track &ldquo;{expData.catLabel}&rdquo; spend.</div>
                )}
              </div>
            )}

            {wsTab === "approval" && (
              <div data-testid="fn-exp-ws-approval">
                <Field label="Current status" inline>
                  <span className="text-sm font-semibold" style={{ color: STATUS_TINT[selected.status as string]?.fg }}>
                    {STATUS_TINT[selected.status as string]?.label ?? selected.status}
                  </span>
                </Field>
                <Field label="Owner"        inline><span className="text-sm">{(selected as unknown as { owner?: string }).owner || "—"}</span></Field>
                <Field label="Approved by"  inline><span className="text-sm">{expData.vp?.approvedBy || "Pending"}</span></Field>
                <div className="mt-3 text-[10px] uppercase tracking-[0.14em] mb-1.5" style={{ color: "var(--ph-ink-muted)" }}>Chain</div>
                <div className="text-sm" style={{ color: "var(--ph-ink-default)" }}>
                  Draft → Submitted → Approved → Scheduled → Paid.
                  {selected.status === "overdue" && " Currently OVERDUE — escalate to owner."}
                </div>
              </div>
            )}

            {wsTab === "attachments" && (
              <div data-testid="fn-exp-ws-attachments">
                <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>Receipts, invoices and supporting documents are managed via Document Actions above (Expense Voucher PDF).</div>
                <DocumentActions docKey="expense-voucher" recordId={selected.id} testId={`fn-exp-attach-${selected.id}`} compact />
              </div>
            )}

            {wsTab === "timeline" && (
              <div data-testid="fn-exp-ws-timeline">
                <Field label="Date"           inline><span className="text-sm">{selected.paidRelative || (selected as unknown as { dateRelative?: string }).dateRelative || "—"}</span></Field>
                <Field label="Status"         inline><span className="text-sm">{selected.status}</span></Field>
                <AuditTrail collection="expenses" recordId={selected.id} testId="fn-exp-timeline-audit" />
              </div>
            )}

            {wsTab === "audit" && (
              <div data-testid="fn-exp-ws-audit">
                <AuditTrail collection="expenses" recordId={selected.id} testId="fn-exp-audit" />
              </div>
            )}

            <div className="pt-4 flex flex-wrap justify-end gap-2">
              <Button variant="secondary" testId="fn-exp-edit" onClick={() => setEditing(true)}>Edit</Button>
              {((selected.status as unknown as string) === "draft") && (
                <Button variant="secondary" testId="fn-exp-submit" onClick={() => transition("submitted")}>Submit</Button>
              )}
              {((selected.status as unknown as string) === "submitted") && (
                <>
                  <Button testId="fn-exp-approve" onClick={() => transition("approved")}>Approve</Button>
                  <Button variant="secondary" testId="fn-exp-reject" onClick={() => transition("rejected")}>Reject</Button>
                </>
              )}
              {((selected.status as unknown as string) === "scheduled" || (selected.status as unknown as string) === "overdue") && (
                <Button testId="fn-exp-mark-paid" onClick={() => transition("paid")}>Mark paid</Button>
              )}
            </div>
          </div>
        )}
      </Drawer>

      <RecordEditor<Expense>
        open={creating}
        onClose={() => setCreating(false)}
        mode="create"
        title="New expense"
        collection="expenses"
        schema={expenseSchema}
        initial={{ category: "other", vatPct: 14 } as unknown as Partial<Expense>}
        sendForApprovalStatus="submitted"
        testId="fn-exp-new-drawer"
      />

      {selected && (
        <RecordEditor<Expense>
          open={editing}
          onClose={() => setEditing(false)}
          mode="edit"
          title="Edit expense"
          collection="expenses"
          schema={expenseSchema}
          initial={selected as unknown as Partial<Expense>}
          onSaved={(row) => setSelected(row)}
          testId="fn-exp-edit-drawer"
        />
      )}
    </div>
  );
}
